import { useEffect, useRef, useState } from "react";
import { Loader2, Save, Eye, EyeOff, CheckCircle2, Send, AlertTriangle, FlaskConical, ChevronRight, Lock, Unlock, KeyRound, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { useSuperMode } from "../../context/SuperModeContext";
import OrientationPicker from "./OrientationPicker";

const FIELDS = [
    {
        section: "Contact (shown across the public site)",
        items: [
            { k: "whatsapp_number", l: "WhatsApp Number", ph: "917000530316 (no plus, country code first)", help: "Used for the floating WhatsApp button + city CTAs." },
            { k: "head_office_phone", l: "Head Office Phone", ph: "+91 7000530316" },
            { k: "head_office_email", l: "Head Office Email", ph: "anandhometutorials@gmail.com" },
            { k: "head_office_address", l: "Head Office Address", ph: "Ghadi Chowk, Vijay Nagar Jabalpur" },
        ],
    },
    {
        section: "Social Media (footer + header)",
        items: [
            { k: "facebook_url", l: "Facebook URL", ph: "https://facebook.com/anandtutorials" },
            { k: "instagram_url", l: "Instagram URL", ph: "https://instagram.com/anandtutorials" },
            { k: "linkedin_url", l: "LinkedIn URL", ph: "https://linkedin.com/company/anandtutorials" },
            { k: "youtube_url", l: "YouTube URL", ph: "https://youtube.com/@anandtutorials" },
            { k: "twitter_url", l: "Twitter / X URL", ph: "https://twitter.com/anandtutorials" },
        ],
    },
    {
        section: "Makeover Expert Profile (1-on-1 Live Online Makeover Classes)",
        items: [
            { k: "makeover_expert_whatsapp", l: "Expert WhatsApp Number", ph: "918085796268 (no plus)", help: "Used for the 'WhatsApp <phone>' button on all makeover pages + homepage teaser." },
            { k: "makeover_expert_phone", l: "Expert Call Number", ph: "+91 8085796268", help: "Shown on the Call button in the Meet-Your-Expert section." },
            { k: "makeover_expert_instagram_url", l: "Expert Instagram URL", ph: "https://www.instagram.com/ishpreet_kaur" },
            { k: "makeover_expert_google_maps_url", l: "Google Business Profile URL", ph: "https://maps.app.goo.gl/…", help: "Drives the 'View on Google' button on the makeover expert card." },
        ],
    },
    {
        section: "Makeover Page Visibility (show / hide any section instantly — no redeploy)",
        items: [
            { k: "makeover_show_hero", l: "Hero + Book / WhatsApp CTAs", toggle: true, help: "Master hero block at the top of every makeover page." },
            { k: "makeover_show_expert", l: "Meet Your Makeover Expert (Ishpreet Kaur)", toggle: true, help: "Expert bio card with photo, credentials and social CTAs." },
            { k: "makeover_show_pillars", l: "Why-Us 3 Pillars (Live · Certified · Timezone)", toggle: true },
            { k: "makeover_show_categories", l: "Service Categories Grid (10 tracks)", toggle: true },
            { k: "makeover_show_countries", l: "Countries Served Grid (15 markets)", toggle: true },
            { k: "makeover_show_why_learn", l: "Why Learn with Ishpreet Kaur (premium grid)", toggle: true },
            { k: "makeover_show_why_choose", l: "Why Choose Anand Tutorials (premium grid)", toggle: true },
            { k: "makeover_show_trust", l: "Trust · Our Commitment (dark section)", toggle: true },
            { k: "makeover_show_testimonials", l: "Testimonials / Verified Google Rating Block", toggle: true, help: "Shows the verified Google Business rating card + link to reviews. Hide only if you plan to launch dedicated testimonials elsewhere." },
            { k: "makeover_show_faq", l: "FAQ Section (rich-result eligible)", toggle: true },
            { k: "makeover_show_enquiry", l: "Enquiry Form Block", toggle: true, help: "Turn OFF to temporarily pause lead capture (existing leads are unaffected)." },
        ],
    },
    {
        section: "Makeover SEO — Google Business Aggregate Rating (drives JSON-LD & rich snippets)",
        items: [
            { k: "makeover_aggregate_rating", l: "Aggregate Rating (0-5)", ph: "4.7", help: "Sourced strictly from Ishpreet Kaur's verified Google Business Profile. Displayed in AggregateRating schema for Google AI + rich snippets." },
            { k: "makeover_review_count", l: "Verified Review Count on Google", ph: "0 = hide (safest until you have a real count)", help: "Enter the ACTUAL count visible on the Google Business Profile. AggregateRating JSON-LD is injected only when this is > 0 (Google penalises inflated / fabricated counts)." },
        ],
    },
    {
        section: "Website Social-Proof Ticker (real, privacy-safe demo-request feed)",
        items: [
            { k: "social_proof_enabled", l: "Show live demo-request popup on the site", toggle: true, help: "Master switch. When OFF, the floating 'Recently requested a demo' popup is hidden site-wide. Existing demo-registration functionality is unaffected." },
            { k: "social_proof_show_name_initial", l: "Show student first-name initial (e.g. \"A***\")", toggle: true, help: "OFF → the popup reads 'A student from …'. Full names, parent names, phone, email, school, and address are NEVER shown, regardless of this toggle." },
            { k: "social_proof_show_city", l: "Show city", toggle: true, help: "OFF → the popup falls back to country (for foreign leads) or hides the location entirely." },
            { k: "social_proof_show_country", l: "Show country (for non-India leads)", toggle: true },
            { k: "social_proof_show_grade", l: "Show grade (e.g. \"Class 10\")", toggle: true },
            { k: "social_proof_show_board", l: "Show board (e.g. \"CBSE\")", toggle: true },
            { k: "social_proof_show_subjects", l: "Show subjects (max 3, e.g. \"Physics, Chemistry & Maths\")", toggle: true },
            { k: "social_proof_max_age_days", l: "Freshness window (days)", ph: "90", help: "Only demo requests submitted in the last N days are eligible. Older entries are excluded to keep the feed genuinely recent." },
        ],
    },
    {
        section: "Email Notifications — Primary: Gmail SMTP (Wix-style, delivers instantly to your inbox)",
        items: [
            { k: "smtp_user", l: "Your Gmail Address", ph: "anandhometutorials@gmail.com", help: "The Gmail account that will send AND receive notifications. All form submissions get emailed to this address instantly." },
            { k: "smtp_app_password", l: "Gmail App Password (16 characters, no spaces)", ph: "xxxx xxxx xxxx xxxx", secret: true, help: "Generate at myaccount.google.com/apppasswords. Requires 2-Step Verification. This is NOT your Gmail login password. Leave blank to keep existing." },
            { k: "notify_cc_email", l: "CC Email (optional — for a coordinator / team member)", ph: "coordinator@anandtutorials.com", help: "Every new lead email will also be CC'd here. Leave blank to disable." },
        ],
    },
    {
        section: "Email Notifications — Fallback: Resend (used only if Gmail SMTP fails or is not configured)",
        items: [
            { k: "resend_api_key", l: "Resend API Key", ph: "re_••••••", secret: true, help: "Optional fallback. Get your key from resend.com → API Keys. Leave blank to keep existing." },
            { k: "resend_sender_email", l: "Resend Sender Email", ph: "noreply@anandtutorials.com (must be verified in Resend)" },
            { k: "resend_notify_email", l: "Resend Notify Email", ph: "anandhometutorials@gmail.com" },
        ],
    },
    {
        section: "Tracking & Analytics",
        items: [
            { k: "google_analytics_id", l: "Google Analytics 4 Measurement ID", ph: "G-XXXXXXXXXX", help: "Get it from analytics.google.com → Admin → Data Streams. Injected site-wide automatically." },
            { k: "meta_pixel_id", l: "Meta (Facebook) Pixel ID", ph: "1234567890123456", help: "Get from business.facebook.com → Events Manager. 15-16 digit ID. Injected site-wide with automatic PageView tracking." },
            { k: "custom_head_code", l: "Custom Head Code", ph: "<!-- Pasted before </head> — e.g. Hotjar, chat widgets, other pixels -->", textarea: true, help: "Raw HTML / <script> tags. Runs on every page. Only paste code you trust." },
            { k: "custom_body_code", l: "Custom Body Code", ph: "<!-- Pasted right after <body> — e.g. live-chat widgets, tracking noscript fallbacks -->", textarea: true, help: "Runs after the page body loads." },
        ],
    },
    {
        section: "SEO Defaults (fallback when page-level meta is empty)",
        items: [
            { k: "site_default_title", l: "Default Page Title", ph: "Anand Tutorials" },
            { k: "site_default_description", l: "Default Meta Description", ph: "India's most trusted 1-on-1 home and online tuition…", textarea: true },
        ],
    },
    {
        section: "Tutor Registration Source — Toggle between Website Form and Book My Tutor App",
        items: [
            { k: "tutor_registration_mode", l: "Registration Source", select: true, help: "Switch between the Anand Tutorials website tutor form and Book My Tutor app. Changes apply instantly across the entire website — no deployment needed.", options: [
                { value: "website", label: "Anand Tutorials Website Form (default)" },
                { value: "external_app", label: "Book My Tutor App (redirect to external)" },
            ] },
            { k: "tutor_registration_external_url", l: "External App URL (used when mode = Book My Tutor)", ph: "https://www.bookmytutor.com/download", help: "Full http(s) URL the website should redirect tutors to when 'Book My Tutor App' mode is selected. Must include https://" },
        ],
    },
    {
        section: "App Management — Book My Tutor (promo section across the site)",
        items: [
            { k: "app_promo_enabled", l: "Show 'Book My Tutor' Promo Section", toggle: true, help: "Master switch — turn off to instantly hide the App promo on Home, Contact and Become-a-Tutor pages." },
            { k: "app_promo_lead_capture_enabled", l: "Enable Lead Capture Form ('Send me the download link')", toggle: true, help: "When ON, visitors can submit phone+email to receive download links via email. Leads land in Admin → App Leads." },
            { k: "app_promo_heading", l: "Promo Heading", ph: "Manage Classes Seamlessly with Book My Tutor App" },
            { k: "app_promo_subheading", l: "Promo Subheading", ph: "Parents and tutors can easily manage classes, communication, schedules…", textarea: true },
            { k: "app_promo_playstore_url", l: "Google Play Store URL", ph: "https://play.google.com/store/apps/details?id=…" },
            { k: "app_promo_appstore_url", l: "Apple App Store URL", ph: "https://apps.apple.com/app/…" },
        ],
    },
    {
        section: "SEO Insights — GA4 + Google Search Console",
        items: [
            { k: "ga_credentials_json", l: "Google Cloud Service Account JSON", ph: '{"type":"service_account","project_id":"…","private_key":"…",…}', secret: true, textarea: true, help: "Paste the full JSON file content from your service account key. Read-only access only. See the SEO Insights tab for setup instructions. Leave blank to keep existing." },
            { k: "ga4_property_id", l: "GA4 Property ID (numeric)", ph: "123456789", help: "Find at Google Analytics → Admin → Property Settings. NOT the Measurement ID (G-XXXXXX)." },
            { k: "gsc_site_url", l: "Search Console Site URL", ph: "https://anandtutorials.com/  or  sc-domain:anandtutorials.com", help: "The exact URL string shown in Search Console — include trailing slash for URL-prefix or use sc-domain: prefix for Domain properties." },
        ],
    },
];

export default function SettingsPanel({ onJump, hiddenModules = [] } = {}) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState({});
    const [showSecrets, setShowSecrets] = useState({});
    const [secretsMeta, setSecretsMeta] = useState({}); // {resend_api_key: {set, masked}, smtp_app_password: {set, masked}}
    const { refresh: refreshPublicSettings } = useSettings();
    // V40 — Advanced Modules gate. Locked by default on every Site Settings
    // mount. Requires the admin to re-enter their MPIN before the 3 internal
    // modules (Lead Pipeline / Ongoing Tuitions / Tutor Portal) are revealed.
    // Re-using the same Super-Admin MPIN keeps the security model simple.
    const [advancedUnlocked, setAdvancedUnlocked] = useState(false);
    const [advancedPromptOpen, setAdvancedPromptOpen] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/settings")
            .then((r) => {
                const {
                    resend_api_key_masked, resend_api_key_set,
                    smtp_app_password_masked, smtp_app_password_set,
                    ga_credentials_json_masked, ga_credentials_json_set,
                    ...rest
                } = r.data;
                setData(rest);
                setSecretsMeta({
                    resend_api_key: { set: !!resend_api_key_set, masked: resend_api_key_masked || "" },
                    smtp_app_password: { set: !!smtp_app_password_set, masked: smtp_app_password_masked || "" },
                    ga_credentials_json: { set: !!ga_credentials_json_set, masked: ga_credentials_json_masked || "" },
                });
            })
            .catch(() => toast.error("Failed to load settings"))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const setField = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
    const setToggle = (k) => (val) => setData((d) => ({ ...d, [k]: val }));

    const save = async () => {
        setSaving(true);
        try {
            // Strip empty strings (backend treats missing = unchanged)
            // and drop resend_api_key when blank so existing key is retained.
            const payload = {};
            Object.entries(data).forEach(([k, v]) => {
                if (v === "" || v === null || v === undefined) return;
                payload[k] = v;
            });
            if (Object.keys(payload).length === 0) {
                toast.info("No changes to save");
                setSaving(false);
                return;
            }
            await api.put("/admin/settings", payload);
            toast.success("Settings saved — live on the public site");
            load();
            // Refresh the global public settings so Footer / Navbar / CTAs update instantly
            refreshPublicSettings();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? `Failed to save: ${msg}` : "Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>;

    return (
        <div data-testid="admin-settings">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold">Site Settings</h2>
                    <p className="text-sm text-slate-500 mt-1">Edit public contact info, social links, and email integration. Changes apply across the entire public site instantly.</p>
                </div>
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="settings-save"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1E3A8A] text-white font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Saving…" : "Save Settings"}
                </button>
            </div>

            <EmailHealthBanner />

            <LeadRecipientsSection />

            <InvoiceTermsSection />

            {/* V40 — Advanced Modules: MPIN-gated container for internal /
                work-in-progress modules (Lead Pipeline, Ongoing Tuitions,
                Tutor Portal). Locked by default on every Site Settings mount.
                The admin must re-enter their Super-Admin MPIN to reveal the
                3 internal modules — same MPIN flow as Super Admin Mode. */}
            {hiddenModules.length > 0 && (
                <div className="mb-6" data-testid="settings-advanced-modules">
                    {!advancedUnlocked ? (
                        // Locked folder card — single clickable tile
                        <button
                            type="button"
                            onClick={() => setAdvancedPromptOpen(true)}
                            data-testid="advanced-modules-locked-folder"
                            className="w-full text-left group rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100 p-5 sm:p-6 transition shadow-sm hover:shadow-md"
                        >
                            <div className="flex items-start gap-4">
                                <div className="shrink-0 w-14 h-14 rounded-2xl bg-amber-200 text-amber-900 flex items-center justify-center group-hover:scale-105 transition">
                                    <FlaskConical className="w-7 h-7" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h3 className="font-heading font-bold text-[#0F172A] text-base sm:text-lg">Advanced Modules</h3>
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-900 bg-amber-200 px-2 py-0.5 rounded-full">
                                            <Lock className="w-2.5 h-2.5" /> Locked
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-rose-700 bg-rose-100 border border-rose-200 px-2 py-0.5 rounded-full">
                                            Super Admin Only
                                        </span>
                                    </div>
                                    <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                                        Contains internal / work-in-progress modules — Lead Pipeline, Ongoing Tuitions, and Tutor Portal.
                                        Protected by MPIN for additional security, similar to Super Admin Mode.
                                    </p>
                                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 bg-white border border-amber-300 px-3 py-1.5 rounded-full">
                                        <KeyRound className="w-3.5 h-3.5" /> Tap to unlock with MPIN
                                        <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
                                    </div>
                                </div>
                            </div>
                        </button>
                    ) : (
                        // Unlocked view — show the 3 module cards
                        <div className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-amber-50 p-5 sm:p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="shrink-0 w-10 h-10 rounded-xl bg-emerald-200 text-emerald-900 flex items-center justify-center">
                                    <Unlock className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-heading font-bold text-[#0F172A] text-base">Advanced Modules</h3>
                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-900 bg-emerald-200 px-2 py-0.5 rounded-full">
                                            <Unlock className="w-2.5 h-2.5" /> Unlocked
                                        </span>
                                        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-rose-700 bg-rose-100 border border-rose-200 px-2 py-0.5 rounded-full">
                                            Internal · Work in Progress
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                                        Hidden from the main Admin navigation but fully operational in the backend.
                                        Modules will return to the sidebar once feature-complete.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setAdvancedUnlocked(false)}
                                    data-testid="advanced-modules-lock-button"
                                    className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-white border border-amber-300 hover:border-amber-500 px-2.5 py-1 rounded-full transition"
                                    title="Lock Advanced Modules"
                                >
                                    <Lock className="w-3 h-3" /> Lock
                                </button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {hiddenModules.map((m) => {
                                    const Icon = m.icon;
                                    return (
                                        <button
                                            key={m.id}
                                            type="button"
                                            onClick={() => onJump?.(m.id)}
                                            disabled={!onJump}
                                            data-testid={`settings-advanced-module-${m.id}`}
                                            className="group text-left bg-white border border-amber-200 rounded-xl p-4 hover:border-amber-400 hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 text-amber-800 flex items-center justify-center">
                                                    <Icon className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="font-heading font-bold text-sm text-[#0F172A]">{m.label}</span>
                                                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-amber-700 transition shrink-0" />
                                                    </div>
                                                    {m.desc && (
                                                        <p className="text-[11px] text-slate-600 mt-1 leading-snug">{m.desc}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <AdvancedModulesMpinPrompt
                        open={advancedPromptOpen}
                        onClose={() => setAdvancedPromptOpen(false)}
                        onUnlocked={() => { setAdvancedUnlocked(true); setAdvancedPromptOpen(false); }}
                    />
                </div>
            )}

            <div className="space-y-6">
                {/* V49 — Display Settings: Screen Orientation (per-device, localStorage) */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="display-settings-section">
                    <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
                        <h3 className="font-heading font-semibold text-[#0F172A] text-sm uppercase tracking-[0.15em]">
                            Display Settings · Screen Orientation
                        </h3>
                        <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                            Saved on this device
                        </span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4 max-w-xl">
                        Control how the admin dashboard behaves when you rotate your phone or tablet. <strong>Portrait Only</strong> is recommended because the mobile dashboard layout is portrait-first. Landscape Only and Auto Rotate are available if you prefer wider data tables on a tablet.
                    </p>
                    <OrientationPicker />
                </div>

                {FIELDS.map((group) => (
                    <div key={group.section} className="bg-white border border-slate-200 rounded-2xl p-6">
                        <h3 className="font-heading font-semibold text-[#0F172A] mb-4 text-sm uppercase tracking-[0.15em]">{group.section}</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {group.items.map((f) => (
                                <div key={f.k} className={f.textarea || f.toggle ? "md:col-span-2" : ""}>
                                    <label className="text-xs font-semibold text-slate-600 mb-1.5 block">{f.l}</label>
                                    {f.toggle ? (
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={data[f.k] !== false}
                                            onClick={() => setToggle(f.k)(data[f.k] === false)}
                                            data-testid={`settings-${f.k}`}
                                            className={`relative inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${data[f.k] !== false ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-50 border-slate-300 text-slate-500"}`}
                                        >
                                            <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${data[f.k] !== false ? "bg-emerald-500" : "bg-slate-300"}`}>
                                                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${data[f.k] !== false ? "translate-x-4" : ""}`} />
                                            </span>
                                            <span className="text-xs font-semibold">{data[f.k] !== false ? "Enabled — visible on public site" : "Disabled — section hidden"}</span>
                                        </button>
                                    ) : f.secret ? (
                                        <div className="relative">
                                            <input
                                                type={showSecrets[f.k] ? "text" : "password"}
                                                value={data[f.k] || ""}
                                                onChange={setField(f.k)}
                                                placeholder={secretsMeta[f.k]?.set ? `Currently: ${secretsMeta[f.k].masked} — enter new value to replace` : f.ph}
                                                data-testid={`settings-${f.k}`}
                                                className="w-full px-3 py-2.5 pr-20 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowSecrets((s) => ({ ...s, [f.k]: !s[f.k] }))}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                                            >
                                                {showSecrets[f.k] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                            {secretsMeta[f.k]?.set && (
                                                <div className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600">
                                                    <CheckCircle2 className="w-3.5 h-3.5" /> Configured ({secretsMeta[f.k].masked})
                                                </div>
                                            )}
                                        </div>
                                    ) : f.textarea ? (
                                        <textarea
                                            rows={3}
                                            value={data[f.k] || ""}
                                            onChange={setField(f.k)}
                                            placeholder={f.ph}
                                            data-testid={`settings-${f.k}`}
                                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                        />
                                    ) : f.select ? (
                                        <select
                                            value={data[f.k] || (f.options?.[0]?.value || "")}
                                            onChange={setField(f.k)}
                                            data-testid={`settings-${f.k}`}
                                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                        >
                                            {f.options.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            value={data[f.k] || ""}
                                            onChange={setField(f.k)}
                                            placeholder={f.ph}
                                            data-testid={`settings-${f.k}`}
                                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#3B82F6]"
                                        />
                                    )}
                                    {f.help && <div className="text-xs text-slate-400 mt-1">{f.help}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ProviderCard({ ok, pending, name, description, icon, children }) {
    // ok=true → green ✓ configured. pending=true → amber ⚠ pending. else → red ❌ not configured.
    const tone = ok
        ? { wrap: "bg-emerald-50 border-emerald-200", title: "text-emerald-800", icon: "text-emerald-600", badge: "✅", label: "Configured" }
        : pending
            ? { wrap: "bg-amber-50 border-amber-200", title: "text-amber-900", icon: "text-amber-600", badge: "⚠️", label: "Configuration pending" }
            : { wrap: "bg-rose-50 border-rose-200", title: "text-rose-900", icon: "text-rose-600", badge: "❌", label: "Not configured" };
    return (
        <div className={`border rounded-2xl p-4 ${tone.wrap}`} data-testid={`provider-card-${name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start gap-3">
                <div className="text-xl shrink-0">{tone.badge}</div>
                <div className="flex-1 min-w-0">
                    <div className={`font-heading font-semibold ${tone.title}`}>
                        {name} — {tone.label}
                    </div>
                    <div className="text-xs text-slate-700 mt-1 leading-relaxed">{description}</div>
                    {children}
                </div>
            </div>
        </div>
    );
}

function EmailHealthBanner() {
    const [status, setStatus] = useState(null);
    const [testEmail, setTestEmail] = useState("");
    const [sending, setSending] = useState(false);

    const refresh = () => {
        api.get("/admin/email/status").then((r) => setStatus(r.data)).catch(() => {});
    };
    useEffect(refresh, []);

    if (!status) return null;

    const sendTest = async () => {
        if (!testEmail) {
            toast.error("Enter an email to send the test to.");
            return;
        }
        setSending(true);
        try {
            const r = await api.post("/admin/email/test", { to: testEmail });
            toast.success(r.data.message);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send test email");
        } finally {
            setSending(false);
        }
    };

    // Per-provider readiness
    const gmailOk = !!status.gmail_smtp_ready;
    // "Pending" = user started configuring but it's incomplete (e.g., password saved but address missing)
    const gmailPending = !gmailOk && !!status.smtp_app_password_set;

    const resendOk = !!status.resend_api_key_set && !!status.sender_email;
    const resendPending = !!status.resend_api_key_set && !resendOk;

    const anyReady = gmailOk || resendOk;
    const primary = status.primary_provider === "gmail_smtp" ? "Gmail SMTP" : status.primary_provider === "resend" ? "Resend" : null;

    return (
        <div className="mb-6 space-y-3" data-testid="email-health-banner">
            {/* Roll-up line */}
            <div className={`px-4 py-2.5 rounded-xl border text-sm ${anyReady ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                {anyReady ? (
                    <>
                        <strong>Email pipeline is live</strong> — leads delivered via <strong>{primary}</strong>. See the <strong>Lead Email Recipients</strong> section below to control which inboxes receive new leads.
                    </>
                ) : (
                    <>
                        <strong>Email pipeline is NOT live.</strong> Form submissions are being saved to the database but no email is being sent. Configure at least one provider below.
                    </>
                )}
            </div>

            {/* Two independent provider cards */}
            <div className="grid md:grid-cols-2 gap-3">
                <ProviderCard
                    ok={gmailOk}
                    pending={gmailPending}
                    name="Gmail SMTP"
                    description={gmailOk
                        ? <>Sending from <code className="bg-white/60 px-1 rounded">{status.smtp_user}</code>. Wix-style direct delivery — instant to your inbox.</>
                        : gmailPending
                            ? <>App Password saved but Gmail SMTP still isn't ready. {status.diagnostic || "Fill in the 'Your Gmail Address' field below — both fields are required."}</>
                            : <>Recommended primary provider. <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] font-semibold underline">Generate an App Password</a> (requires 2-Step Verification) → paste your Gmail address AND the 16-char code below → Save.</>
                    }
                />
                <ProviderCard
                    ok={resendOk}
                    pending={resendPending}
                    name="Resend"
                    description={resendOk
                        ? <>API key configured · sender <code className="bg-white/60 px-1 rounded">{status.sender_email}</code>. Used as fallback if Gmail SMTP fails.</>
                        : resendPending
                            ? <>API key saved but Resend isn't ready — make sure a verified sender email is set in the Resend section below.</>
                            : <>Optional fallback provider. Sign up free at <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#1E3A8A] font-semibold underline">resend.com</a> → verify a domain (Resend rejects unverified gmail.com senders) → paste API key + sender below → Save.</>
                    }
                />
            </div>

            {/* Send Test Email — only meaningful if at least one provider is ready */}
            {anyReady && (
                <div className="mt-2 flex flex-wrap items-center gap-2 px-4 py-3 rounded-xl bg-white border border-slate-200">
                    <span className="text-xs font-semibold text-slate-700 mr-1">Verify delivery:</span>
                    <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="anandhometutorials@gmail.com"
                        data-testid="settings-test-email-input"
                        className="flex-1 min-w-[220px] px-3 py-1.5 text-xs rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                    <button
                        onClick={sendTest}
                        disabled={sending}
                        data-testid="settings-send-test-email"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                    >
                        {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        {sending ? "Sending…" : "Send Test Email"}
                    </button>
                </div>
            )}
        </div>
    );
}


/**
 * V40 — AdvancedModulesMpinPrompt
 *
 * Lightweight MPIN re-verification modal that gates access to the Advanced
 * Modules folder inside Site Settings. Visually mirrors the Super Admin
 * Mode unlock modal but is intentionally scoped:
 *   • Re-uses the same `superMode.unlock(mpin)` flow to validate the MPIN
 *     against the server (so we don't reinvent crypto here). On success
 *     we simply flip a local `advancedUnlocked` flag in the parent.
 *   • A wrong MPIN, an empty MPIN, or closing the modal all leave the
 *     Advanced Modules locked.
 */
function AdvancedModulesMpinPrompt({ open, onClose, onUnlocked }) {
    const superMode = useSuperMode();
    const inputRef = useRef(null);
    const [mpin, setMpin] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setMpin(""); setError(""); setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 80);
    }, [open]);

    if (!open) return null;

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!/^\d{4,6}$/.test(mpin)) {
            setError("Enter your 4-6 digit Super Admin MPIN.");
            return;
        }
        setBusy(true);
        try {
            // Re-validates the MPIN against the server. On success the server
            // also refreshes the super-mode session — harmless side-effect.
            await superMode.unlock(mpin);
            toast.success("Advanced Modules unlocked");
            onUnlocked();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || "Incorrect MPIN — access denied.";
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    const close = () => { if (!busy) onClose(); };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={close}
            data-testid="advanced-modules-mpin-modal"
        >
            <div
                className="bg-white rounded-2xl max-w-md w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-slate-200 flex items-start gap-3">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                        <Lock className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-bold text-[#0F172A] text-lg">Unlock Advanced Modules</h3>
                        <p className="text-xs text-slate-600 mt-0.5">
                            Re-enter your Super Admin MPIN to access internal modules: Lead Pipeline, Ongoing Tuitions, and Tutor Portal.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        disabled={busy}
                        data-testid="advanced-modules-mpin-close"
                        className="shrink-0 p-1 rounded hover:bg-slate-100 disabled:opacity-50"
                    >
                        <X className="w-4 h-4 text-slate-500" />
                    </button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-600 mb-2 block">
                            Super Admin MPIN
                        </label>
                        <input
                            ref={inputRef}
                            type="password"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={mpin}
                            onChange={(e) => {
                                setMpin((e.target.value || "").replace(/\D/g, "").slice(0, 6));
                                if (error) setError("");
                            }}
                            maxLength={6}
                            placeholder="Enter 4-6 digit MPIN"
                            data-testid="advanced-modules-mpin-input"
                            className="w-full px-4 py-3 text-lg tracking-[0.4em] text-center font-mono rounded-lg border-2 border-slate-200 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200"
                        />
                        {error && (
                            <p
                                className="mt-2 text-xs text-red-600 flex items-center gap-1"
                                data-testid="advanced-modules-mpin-error"
                                role="alert"
                            >
                                <ShieldAlert className="w-3.5 h-3.5" /> {error}
                            </p>
                        )}
                        <p className="mt-2 text-[11px] text-slate-500 leading-snug">
                            Same MPIN as Super Admin Mode. If you don&apos;t remember it, lock Super Admin Mode from the header and use the &quot;Forgot MPIN&quot; flow.
                        </p>
                    </div>

                    <div className="flex gap-2 pt-1">
                        <button
                            type="button"
                            onClick={close}
                            disabled={busy}
                            data-testid="advanced-modules-mpin-cancel"
                            className="flex-1 px-4 py-2.5 border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-semibold rounded-lg disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={busy || mpin.length < 4}
                            data-testid="advanced-modules-mpin-submit"
                            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg disabled:opacity-60"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />}
                            {busy ? "Verifying…" : "Unlock"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ---------------- V172 — Lead Email Recipients ------------------------------
// Admin-managed list of destinations for new-lead notification emails.
// Replaces the old single hard-coded destination. Default new entry is
// `anandtutorials.leads@gmail.com`; the legacy address `anandhometutorials@gmail.com`
// is migrated as DISABLED so no email reaches it until the admin re-enables it.
function LeadRecipientsSection() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]); // [{email, active, _testing}]
    const [defaultEmail, setDefaultEmail] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [dirty, setDirty] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/lead-recipients")
            .then((r) => {
                setItems((r.data.recipients || []).map((x) => ({ ...x, _testing: false })));
                setDefaultEmail(r.data.default_recipient || "");
                setDirty(false);
            })
            .catch(() => toast.error("Failed to load lead recipients"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

    const addEmail = () => {
        const e = newEmail.trim().toLowerCase();
        if (!e) return;
        if (!isValidEmail(e)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        if (items.some((x) => x.email.toLowerCase() === e)) {
            toast.error("That email is already in the list.");
            return;
        }
        setItems((xs) => [...xs, { email: e, active: true, _testing: false }]);
        setNewEmail("");
        setDirty(true);
    };

    const toggleActive = (email) => {
        setItems((xs) => xs.map((x) => (x.email === email ? { ...x, active: !x.active } : x)));
        setDirty(true);
    };

    const removeEmail = (email) => {
        if (items.length <= 1) {
            toast.error("At least one recipient is required. Toggle it OFF instead of removing.");
            return;
        }
        setItems((xs) => xs.filter((x) => x.email !== email));
        setDirty(true);
    };

    const save = async () => {
        if (items.length === 0) {
            toast.error("Add at least one recipient before saving.");
            return;
        }
        if (!items.some((x) => x.active)) {
            if (!window.confirm("No recipient is currently ACTIVE. If you save, new lead emails will fall back to the legacy address. Continue?")) {
                return;
            }
        }
        setSaving(true);
        try {
            const payload = { recipients: items.map((x) => ({ email: x.email, active: !!x.active })) };
            const r = await api.put("/admin/lead-recipients", payload);
            setItems((r.data.recipients || []).map((x) => ({ ...x, _testing: false })));
            setDirty(false);
            toast.success(`Saved. ${r.data.active_count} active recipient${r.data.active_count === 1 ? "" : "s"} will receive new lead emails.`);
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed to save lead recipients.");
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async (email) => {
        setItems((xs) => xs.map((x) => (x.email === email ? { ...x, _testing: true } : x)));
        try {
            const r = await api.post("/admin/lead-recipients/test", { email });
            toast.success(r.data.message || `Test email sent to ${email}`);
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed to send test email.");
        } finally {
            setItems((xs) => xs.map((x) => (x.email === email ? { ...x, _testing: false } : x)));
        }
    };

    if (loading) {
        return (
            <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-center" data-testid="lead-recipients-loading">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
        );
    }

    const activeCount = items.filter((x) => x.active).length;

    return (
        <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="lead-recipients-section">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-[#0F172A] text-sm uppercase tracking-[0.15em] mb-1">
                        Lead Email Recipients
                    </h3>
                    <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
                        Choose which inboxes receive new-lead notification emails. Every ACTIVE recipient gets a dedicated copy of each new lead, demo registration, inquiry and form submission.
                        Toggling a recipient OFF stops future deliveries to that address immediately — historical leads remain unaffected.
                        <span className="block mt-1 text-[11px] text-slate-500">
                            Default: <code className="bg-slate-100 px-1 rounded">{defaultEmail}</code>
                        </span>
                    </p>
                </div>
                <span
                    className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${
                        activeCount > 0
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}
                    data-testid="lead-recipients-active-count"
                >
                    {activeCount} active
                </span>
            </div>

            {/* Recipient list */}
            <div className="space-y-2 mb-4" data-testid="lead-recipients-list">
                {items.length === 0 && (
                    <div className="text-xs text-slate-500 italic px-3 py-2">No recipients configured.</div>
                )}
                {items.map((r) => (
                    <div
                        key={r.email}
                        data-testid={`lead-recipient-row-${r.email}`}
                        data-active={r.active ? "true" : "false"}
                        className={`flex items-center flex-wrap sm:flex-nowrap gap-2 sm:gap-3 border rounded-lg p-2.5 sm:p-3 transition-colors ${
                            r.active ? "bg-emerald-50/40 border-emerald-200" : "bg-slate-50 border-slate-200"
                        }`}
                    >
                        {/* Toggle */}
                        <button
                            type="button"
                            role="switch"
                            aria-checked={r.active}
                            onClick={() => toggleActive(r.email)}
                            data-testid={`lead-recipient-toggle-${r.email}`}
                            className={`relative inline-block w-10 h-5 rounded-full transition-colors shrink-0 ${
                                r.active ? "bg-emerald-500" : "bg-slate-300"
                            }`}
                            title={r.active ? "Click to disable" : "Click to enable"}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${r.active ? "translate-x-5" : ""}`} />
                        </button>
                        {/* Email + status pill */}
                        <div className="flex-1 min-w-0 flex items-center flex-wrap gap-2">
                            <code
                                className={`text-xs sm:text-sm font-mono truncate max-w-full ${r.active ? "text-emerald-900 font-semibold" : "text-slate-500"}`}
                                data-testid={`lead-recipient-email-${r.email}`}
                            >
                                {r.email}
                            </code>
                            <span
                                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                                    r.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"
                                }`}
                            >
                                {r.active ? "Active" : "Inactive"}
                            </span>
                            {r.email.toLowerCase() === defaultEmail.toLowerCase() && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                    Default
                                </span>
                            )}
                        </div>
                        {/* Actions */}
                        <div className="flex items-center gap-1.5 shrink-0">
                            <button
                                type="button"
                                onClick={() => sendTest(r.email)}
                                disabled={r._testing}
                                data-testid={`lead-recipient-test-${r.email}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-white border border-slate-300 hover:border-blue-400 text-slate-700 hover:text-blue-700 disabled:opacity-60"
                                title="Send a test lead-notification email to this address"
                            >
                                {r._testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Test
                            </button>
                            <button
                                type="button"
                                onClick={() => removeEmail(r.email)}
                                data-testid={`lead-recipient-remove-${r.email}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-white border border-slate-300 hover:border-rose-400 text-slate-700 hover:text-rose-700"
                                title="Remove this recipient"
                            >
                                <X className="w-3 h-3" />
                                Remove
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Add email */}
            <div className="flex flex-wrap gap-2 mb-3">
                <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                    placeholder="Add a new recipient — e.g. admissions@anandtutorials.com"
                    data-testid="lead-recipients-new-email-input"
                    className="flex-1 min-w-[240px] px-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                <button
                    type="button"
                    onClick={addEmail}
                    data-testid="lead-recipients-add-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white font-semibold text-xs rounded-lg"
                >
                    <Send className="w-3.5 h-3.5" /> Add Email
                </button>
            </div>

            {/* Save bar */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
                <div className="text-[11px] text-slate-500">
                    {dirty ? (
                        <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> You have unsaved changes
                        </span>
                    ) : (
                        <span className="text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> All changes saved
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || !dirty}
                    data-testid="lead-recipients-save-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs rounded-lg"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {saving ? "Saving…" : "Save Changes"}
                </button>
            </div>
        </div>
    );
}


// ──────────── V173.2 — Invoice Terms & Conditions Templates ─────────────────
// Admin-editable templates for the two primary invoice categories. Each new
// invoice snapshots the matching template at creation time, so historical
// invoices never retro-change when a template is edited later.
function InvoiceTermsSection() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [home, setHome] = useState("");
    const [online, setOnline] = useState("");
    const [defaults, setDefaults] = useState({ home_tuition: "", online_1_to_1: "" });
    const [dirty, setDirty] = useState(false);
    const [active, setActive] = useState("home"); // which tab is open

    const load = () => {
        setLoading(true);
        api.get("/admin/invoice-terms")
            .then((r) => {
                setHome(r.data.home_tuition || "");
                setOnline(r.data.online_1_to_1 || "");
                setDefaults(r.data.defaults || { home_tuition: "", online_1_to_1: "" });
                setDirty(false);
            })
            .catch(() => toast.error("Failed to load invoice T&C templates"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put("/admin/invoice-terms", {
                home_tuition: home,
                online_1_to_1: online,
            });
            setHome(r.data.home_tuition || "");
            setOnline(r.data.online_1_to_1 || "");
            setDirty(false);
            toast.success("Invoice T&C templates saved. New invoices will use the updated text.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to save templates.");
        } finally {
            setSaving(false);
        }
    };

    const restoreDefault = (which) => {
        if (!window.confirm(`Restore the factory default policy for ${which === "home" ? "Home Tuitions" : "1:1 Online Classes"}? Your current edits for this template will be replaced.`)) return;
        if (which === "home") setHome(defaults.home_tuition || "");
        else setOnline(defaults.online_1_to_1 || "");
        setDirty(true);
    };

    if (loading) {
        return (
            <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-center" data-testid="invoice-terms-loading">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
        );
    }

    const activeValue = active === "home" ? home : online;
    const activeSetter = active === "home" ? setHome : setOnline;
    const activeDefault = active === "home" ? defaults.home_tuition : defaults.online_1_to_1;
    const isDefault = (activeValue || "").trim() === (activeDefault || "").trim();

    return (
        <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="invoice-terms-section">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-[#0F172A] text-sm uppercase tracking-[0.15em] mb-1">
                        Invoice Policy Templates
                    </h3>
                    <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
                        Two editable Terms & Conditions templates — one per invoice category. Every <strong>new</strong> invoice automatically snapshots the matching template at creation time, so later edits here never retro-change past invoices.
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-3" role="tablist">
                {[
                    { id: "home", label: "Home Tuitions", testid: "invoice-terms-tab-home" },
                    { id: "online", label: "1:1 Online Classes", testid: "invoice-terms-tab-online" },
                ].map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={active === t.id}
                        onClick={() => setActive(t.id)}
                        data-testid={t.testid}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                            active === t.id
                                ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
                                : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
                {isDefault && (
                    <span className="ml-auto inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-500">
                        Factory default
                    </span>
                )}
            </div>

            {/* Editor */}
            <textarea
                value={activeValue}
                onChange={(e) => { activeSetter(e.target.value); setDirty(true); }}
                data-testid={`invoice-terms-editor-${active}`}
                rows={16}
                className="w-full px-3 py-2 font-mono text-xs leading-relaxed border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/40 whitespace-pre-wrap"
                placeholder="Enter the terms & conditions for this invoice category…"
            />
            <div className="text-[11px] text-slate-500 mt-1">
                {activeValue.length} / 8000 characters · Numbered lists survive as plain text.
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
                <div className="text-[11px] text-slate-500">
                    {dirty ? (
                        <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Unsaved changes
                        </span>
                    ) : (
                        <span className="text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> All changes saved
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => restoreDefault(active)}
                        data-testid={`invoice-terms-restore-${active}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 font-semibold text-xs rounded-lg"
                    >
                        Restore default
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving || !dirty}
                        data-testid="invoice-terms-save-btn"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1E3A8A] hover:bg-[#1e40af] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs rounded-lg"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {saving ? "Saving…" : "Save Templates"}
                    </button>
                </div>
            </div>
        </div>
    );
}

